import styled, {css} from 'styled-components';
import {getColorFromTheme} from '../../ui/ui-utils';
import {motion} from 'framer-motion';
import {Button} from '@ui/atoms/button';

export const EditHeader = styled.div`
  position: sticky;
  z-index: 20;
  top: 0;
  display: flex;
  min-height: 80px;
  align-items: center;
  justify-content: space-between;
  flex: 0 0 auto;
  gap: 24px;
  padding: 18px 24px;
  margin: 0 -24px;
  border-bottom: 1px solid ${getColorFromTheme('borderLight')};
  background: ${getColorFromTheme('white')};
  isolation: isolate;
`;

export const EditTitle = styled.h2`
  margin: 0;
  color: ${getColorFromTheme('blackTwo')};
  font: 600 20px/28px Graphik, sans-serif;
`;

export const EditCloseButton = styled.button`
  display: inline-flex;
  width: 103px;
  height: 42px;
  align-items: center;
  justify-content: space-between;
  flex: 0 0 auto;
  padding: 10px 16px;
  border: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};
  color: ${getColorFromTheme('blackTwo')};
  font: 600 14px/22px Graphik, sans-serif;
  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
    flex: 0 0 auto;
  }

  &:focus-visible {
    outline: 2px solid ${getColorFromTheme('purple300')};
    outline-offset: 2px;
  }
`;

export const FormSection = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 8px;
`;

export const FormStack = styled.div<{$sidePanel?: boolean}>`
  display: flex;
  width: 100%;
  height: ${({$sidePanel}) => ($sidePanel ? '100%' : 'auto')};
  flex-direction: column;
  gap: 24px;
  padding: ${({$sidePanel}) => ($sidePanel ? '0 24px 100px' : '0')};
  overflow-y: ${({$sidePanel}) => ($sidePanel ? 'auto' : 'visible')};
`;

export const FormActions = styled.div<{$sidePanel?: boolean}>`
  ${({$sidePanel}) =>
    $sidePanel
      ? css`
          display: flex;
          position: fixed;
          z-index: 20;
          right: 0;
          bottom: 0;
          width: min(560px, 100vw);
          padding: 16px 24px 24px;
          border-top: 1px solid ${getColorFromTheme('borderLight')};
          background: ${getColorFromTheme('white')};
        `
      : css`
          display: contents;
        `}

  > button {
    width: 100%;
  }
`;

export const CreateGoalSubmitButton = styled(Button)`
  &:disabled {
    background-color: ${({isLoading, theme}) =>
      isLoading ? theme.colors.purple300 : theme.colors.backgroundDark};
    border-color: ${({isLoading, theme}) =>
      isLoading ? theme.colors.purple300 : theme.colors.backgroundDark};
    color: ${({isLoading, theme}) =>
      isLoading ? theme.colors.alwaysWhite : theme.colors.textMuted};
  }
`;

export const ResultsSection = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 16px;
`;

export const FormRow = styled.div`
  display: grid;
  width: 100%;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 640px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 24px;
  }
`;

export const SegmentedControl = styled.div`
  display: inline-flex;
  width: fit-content;
  align-items: center;
  gap: 2px;
  padding: 4px;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 8px;
  background: ${getColorFromTheme('backgroundDark')};
`;

export const SegmentedControlButton = styled.button<{active?: boolean}>`
  display: inline-flex;
  min-height: 32px;
  align-items: center;
  justify-content: center;
  padding: 5px 12px;
  border: 0;
  border-radius: 7px;
  background: ${({active}) =>
    active ? getColorFromTheme('white') : 'transparent'};
  box-shadow: ${({active, theme}) =>
    active ? theme.elevation.level2 : 'none'};
  color: ${({active}) =>
    active ? getColorFromTheme('purple300') : getColorFromTheme('textBody')};
  font-family: Graphik, sans-serif;
  font-size: 14px;
  font-weight: ${({active}) => (active ? 500 : 400)};
  line-height: 22px;
  white-space: nowrap;
  cursor: pointer;

  &:disabled {
    color: ${getColorFromTheme('textMuted')};
    cursor: not-allowed;
  }
`;

export const OptionalAction = styled.button`
  display: inline-flex;
  width: fit-content;
  align-items: center;
  gap: 8px;
  border: 0;
  background: transparent;
  color: ${getColorFromTheme('purple300')};
  font-family: Graphik, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  cursor: pointer;
`;

export const OptionalPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  border-radius: 8px;
  background: ${getColorFromTheme('backgroundDark')};
  color: ${getColorFromTheme('textBody')};
  font-size: 12px;
  font-weight: 400;
  line-height: 20px;
`;

export const Divider = styled.hr`
  width: 100%;
  margin: 0;
  border: 0;
  border-top: 1px solid ${getColorFromTheme('borderLight')};
`;

export const KpiValueInputShell = styled.div<{hasError?: boolean}>`
  display: flex;
  width: 100%;
  min-height: 44px;
  border: 1px solid
    ${({hasError}) =>
      hasError
        ? getColorFromTheme('red400')
        : getColorFromTheme('borderLight')};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};
  overflow: hidden;

  &:focus-within {
    border-color: ${getColorFromTheme('purple300')};
  }
`;

export const KpiValuePrefix = styled.span`
  display: inline-flex;
  min-width: 52px;
  align-items: center;
  justify-content: center;
  border-right: 1px solid ${getColorFromTheme('borderLight')};
  background: ${getColorFromTheme('backgroundDark')};
  color: ${getColorFromTheme('textBody')};
  font-size: 14px;
  line-height: 22px;
`;

export const KpiValueInput = styled.input`
  width: 100%;
  min-width: 0;
  padding: 10px 16px;
  border: 0;
  background: transparent;
  color: ${getColorFromTheme('textDark')};
  font-family: Graphik, sans-serif;
  font-size: 14px;
  line-height: 22px;

  &::placeholder {
    color: ${getColorFromTheme('textBody')};
  }

  &:focus {
    outline: none;
  }

  &:read-only {
    background: ${getColorFromTheme('backgroundDark')};
    color: ${getColorFromTheme('textBody')};
    cursor: not-allowed;
  }
`;

export const KpiMetricFields = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 16px;
`;

export const KpiMetricSelectorRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 8px;
`;

export const KpiCurrencySelect = styled.div`
  position: relative;
  height: 40px;

  select {
    height: 40px;
    padding: 3px 38px 3px 12px;
    border: 0;
    border-radius: 8px;
    appearance: none;
    background: ${getColorFromTheme('white')};
    box-shadow: ${({theme}) => theme.elevation.level2};
    color: ${getColorFromTheme('textDark')};
    font-family: Graphik, sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid ${getColorFromTheme('purple300')};
      outline-offset: 1px;
    }
  }

  svg {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 16px;
    height: 16px;
    pointer-events: none;
    stroke: ${getColorFromTheme('textDark')};
  }
`;

export const KpiValueRow = styled.div`
  display: grid;
  width: 100%;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 640px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const KpiSegmentedControlButton = styled(SegmentedControlButton)`
  height: 32px;
  min-height: 32px;
  padding: 3px 12px;
  color: ${({active, theme}) =>
    active ? theme.colors.textDark : theme.colors.textBody};
`;

export const InfoPanel = styled.div`
  width: 100%;
  padding: 16px 24px;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 10px;
  background: ${getColorFromTheme('backgroundDark')};
  color: ${getColorFromTheme('textBody')};
  font-size: 14px;
  line-height: 22px;

  strong {
    color: ${getColorFromTheme('textDark')};
    font-weight: 600;
  }
`;

export const ParentGoalPanel = styled.div`
  width: 100%;
  padding: 20px;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};
`;

export const ParentGoalSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

export const ParentGoalHeader = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
  padding: 0;
`;

export const ParentGoalCollapse = styled.button`
  align-items: center;
  background: transparent;
  border: 0;
  color: ${getColorFromTheme('red400')};
  cursor: pointer;
  display: inline-flex;
  font: 600 14px/22px Graphik, sans-serif;
  gap: 10px;
  padding: 0;

  svg {
    height: 16px;
    width: 16px;
  }

  svg path {
    stroke: currentColor;
  }

  &:focus-visible {
    border-radius: 4px;
    outline: 2px solid rgba(213, 42, 42, 0.18);
    outline-offset: 3px;
  }

  &:disabled {
    color: ${getColorFromTheme('textMuted')};
    cursor: not-allowed;
  }
`;

export const ParentGoalDescription = styled.p`
  color: ${getColorFromTheme('textBody')};
  font: 400 14px/22px Graphik, sans-serif;
  margin: 0 0 16px;
`;

export const ParentGoalContribution = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
`;

export const RemoveAlignmentButton = styled.button`
  display: inline-flex;
  width: fit-content;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${getColorFromTheme('red400')};
  font: 600 14px/22px Graphik, sans-serif;
  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
  }

  svg path {
    stroke: currentColor;
  }

  &:focus-visible {
    border-radius: 4px;
    outline: 2px solid rgba(213, 42, 42, 0.18);
    outline-offset: 3px;
  }
`;

export const AlignGoalWrapper = styled.div`
  display: block;
  border: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 10px;
  width: 100%;

  &:hover {
    box-shadow: ${getColorFromTheme('purple200')} 0 0 0 2px;
  }
`;

export const AlignGoalHeader = styled.a`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
`;

export const MeasurementWrapper = styled.div<{error?: boolean}>`
  border: 1px solid
    ${({error}) =>
      error ? getColorFromTheme('red400') : getColorFromTheme('borderDark')};
  padding: 16px;
  margin-top: 10px;
  border-radius: 10px;
`;

export const AlignGoalChild = styled(motion.div)`
  border-top: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 10px;
`;

export const QuestionIconWrapper = styled.a<{tooltip?: boolean}>`
  outline: none;
  position: relative;

  svg {
    width: 14px;
    height: 14px;
    stroke: ${getColorFromTheme('purple300')};
    margin-bottom: -2px;
  }

  @media (max-width: 500px) {
    display: none;
  }
  .tooltip {
    visibility: hidden;
    width: 399px;
    background-color: ${getColorFromTheme('white')};
    font-size: 14px;
    font-weight: normal;

    color: ${getColorFromTheme('textBody')};
    text-align: center;
    padding: 16px 16px 16px 26px;
    border: 1px solid ${getColorFromTheme('purple200')};
    border-radius: 5px;
    position: absolute;
    opacity: 0;
    left: -130px;
    transition: opacity 0.3s;
    z-index: 3;

    margin-top: -130px;
  }
  &:hover .tooltip {
    visibility: ${(props: any) => (props.tooltip === true ? 'visible' : '')};
    opacity: 1;
  }

  .tooltip::before {
    content: ' ';
    position: absolute;
    top: 100%;
    left: 33.1%;
    margin-left: -5px;
    border-width: 11px;
    border-style: solid;
    border-color: ${getColorFromTheme('purple200')} transparent transparent
      transparent;
  }

  .tooltip::after {
    content: ' ';
    position: absolute;
    top: 100%;
    left: 33.3%;
    margin-left: -5px;
    border-width: 10px;
    border-style: solid;
    border-color: ${getColorFromTheme('white')} transparent transparent
      transparent;
  }
`;
